import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormGroupDirective, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { filter, finalize } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { RestaurantComment } from '../../../interfaces/restaurant';
import { RestaurantService } from '../../../services/restaurant/restaurant.service';
import { ConfirmationDialogComponent } from '../../modals/confirm-dialog/confirmation-dialog.component';

const nonWhitespaceValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;

  return typeof value === 'string' && value.length > 0 && value.trim().length === 0
    ? { whitespace: true }
    : null;
};

@UntilDestroy()
@Component({
  selector: 'app-restaurant-comments',
  templateUrl: './restaurant-comments.component.html',
  styleUrls: ['./restaurant-comments.component.scss']
})
export class RestaurantCommentsComponent implements OnChanges {
  @Input() restaurantId: number | null = null;
  @Input() canAdd = false;

  public readonly authorNameMaxLength = 255;
  public readonly bodyMaxLength = 10000;
  public readonly commentForm: FormGroup = this.formBuilder.group({
    authorName: ['', [Validators.required, Validators.maxLength(this.authorNameMaxLength), nonWhitespaceValidator]],
    body: ['', [Validators.required, Validators.maxLength(this.bodyMaxLength), nonWhitespaceValidator]],
  });

  public comments: RestaurantComment[] = [];
  public loading = false;
  public submitting = false;
  public confirmingPublication = false;
  public loadError: string | null = null;
  public submitError: string | null = null;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly restaurantService: RestaurantService,
    private readonly translate: TranslateService,
    private readonly matDialog: MatDialog,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.restaurantId && this.restaurantId) {
      this.loadComments();
    }
  }

  public submitComment(formDirective: FormGroupDirective): void {
    if (!this.canAdd || this.submitting || this.confirmingPublication) {
      return;
    }

    this.commentForm.markAllAsTouched();

    if (this.commentForm.invalid || !this.restaurantId) {
      return;
    }

    this.submitError = null;
    this.confirmCommentPublication(formDirective);
  }

  private confirmCommentPublication(formDirective: FormGroupDirective): void {
    this.confirmingPublication = true;

    const confirmationModal = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant('pages.restaurantAccount.comments.confirmTitle'),
        message: this.translate.instant('pages.restaurantAccount.comments.confirmMessage'),
      },
      panelClass: 'confirmation-modal'
    });

    confirmationModal.afterClosed().pipe(
      finalize(() => this.confirmingPublication = false),
      filter(Boolean),
      untilDestroyed(this),
    ).subscribe(() => {
      this.createComment(formDirective);
    });
  }

  private createComment(formDirective: FormGroupDirective): void {
    if (!this.restaurantId) {
      return;
    }

    this.submitting = true;
    this.submitError = null;

    this.restaurantService.createComment(this.restaurantId, {
      authorName: this.commentForm.value.authorName.trim(),
      body: this.commentForm.value.body.trim(),
      section: 'admin',
    }).pipe(
      finalize(() => this.submitting = false),
      untilDestroyed(this),
    ).subscribe((comment) => {
      this.comments = [comment, ...this.comments];
      formDirective.resetForm({ authorName: '', body: '' });
    }, (error: HttpErrorResponse) => {
      this.submitError = this.getLocalizedError(
        error,
        this.translate.instant('pages.restaurantAccount.comments.submitError')
      );
    });
  }

  public trackByCommentId(index: number, comment: RestaurantComment): number {
    return comment.id;
  }

  private loadComments(): void {
    if (!this.restaurantId) {
      return;
    }

    this.loading = true;
    this.loadError = null;
    this.comments = [];

    this.restaurantService.getComments(this.restaurantId, 'admin').pipe(
      finalize(() => this.loading = false),
      untilDestroyed(this),
    ).subscribe((comments) => {
      this.comments = [...comments].sort(
        (first, second) => new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime()
      );
    }, (error: HttpErrorResponse) => {
      this.loadError = this.getLocalizedError(
        error,
        this.translate.instant('pages.restaurantAccount.comments.loadError')
      );
    });
  }

  private getLocalizedError(error: HttpErrorResponse, fallback: string): string {
    const responseError = error.error;
    const sources = responseError?.violations?.length ? responseError.violations : [responseError];
    const language = localStorage.getItem('lang') ?? this.translate.currentLang ?? 'en-CA';
    const messages = sources
      .map((source: any) => source?.translation?.[language] ?? source?.message)
      .filter((message: unknown): message is string => typeof message === 'string' && message.length > 0);

    return messages.length ? messages.join(' ') : fallback;
  }
}
